type AppErrorCopy = {
  statusMessage: string
  message: string
  description: string
}

export type AppErrorData = {
  description?: string
  retryTo?: string
  retryLabel?: string
  backTo?: string
  backLabel?: string
  request?: string
} & Record<string, unknown>

export type AppErrorOptions = {
  statusCode?: number
  statusMessage?: string
  message?: string
  description?: string
  fatal?: boolean
  retryTo?: string
  retryLabel?: string
  backTo?: string
  backLabel?: string
  request?: RequestInfo | URL | string
  data?: Record<string, unknown>
  cause?: unknown
}

const defaultErrorCopy: AppErrorCopy = {
  statusMessage: 'Application Error',
  message: 'Something went wrong.',
  description: 'The application hit an unexpected problem. Please try again.',
}

const errorCopyByStatusCode: Record<number, AppErrorCopy> = {
  400: {
    statusMessage: 'Bad Request',
    message: 'The request could not be completed.',
    description: 'Check the submitted data and try again.',
  },
  401: {
    statusMessage: 'Unauthorized',
    message: 'Your session is no longer authorized.',
    description: 'Sign in again or return to a safe page before retrying.',
  },
  403: {
    statusMessage: 'Forbidden',
    message: 'You do not have access to this resource.',
    description: 'Go back to a page you can access or contact an administrator.',
  },
  404: {
    statusMessage: 'Not Found',
    message: 'The requested resource could not be found.',
    description: 'It may have been removed or the link may be outdated.',
  },
  422: {
    statusMessage: 'Validation Error',
    message: 'The submitted data did not pass validation.',
    description: 'Review the data and try again.',
  },
  500: {
    statusMessage: 'Server Error',
    message: 'The server failed to process the request.',
    description: 'Retry in a moment. If the issue keeps happening, inspect the backend logs.',
  },
  503: {
    statusMessage: 'Service Unavailable',
    message: 'The service is temporarily unavailable.',
    description: 'Wait a moment and retry the request.',
  },
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const readString = (value: unknown) => {
  return typeof value === 'string' && value.trim() ? value : undefined
}

const readNumber = (value: unknown) => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

const getRequestPath = (value: RequestInfo | URL | string | undefined) => {
  if (!value) {
    return undefined
  }

  if (typeof value === 'string') {
    return value
  }

  if (typeof URL !== 'undefined' && value instanceof URL) {
    return value.toString()
  }

  if (typeof Request !== 'undefined' && value instanceof Request) {
    return value.url
  }

  return undefined
}

const getErrorDescription = (value: unknown) => {
  if (!isRecord(value)) {
    return undefined
  }

  return (
    readString(value.description)
    ?? readString(value.detail)
    ?? readString(value.error_description)
    ?? readString(value.error)
    ?? readString(value.message)
  )
}

const getErrorCopy = (statusCode: number) => {
  return errorCopyByStatusCode[statusCode] ?? defaultErrorCopy
}

export const resolveAppErrorCopy = (statusCode?: number) => {
  return getErrorCopy(statusCode ?? 500)
}

export function useAppError() {
  const route = useRoute()

  const createAppError = (input: unknown, options: AppErrorOptions = {}) => {
    const inputRecord = isRecord(input) ? input : null
    const response = isRecord(inputRecord?.response) ? inputRecord.response : null
    const responseData = response?._data

    const statusCode = (
      options.statusCode
      ?? readNumber(inputRecord?.statusCode)
      ?? readNumber(inputRecord?.status)
      ?? readNumber(response?.status)
      ?? 500
    )
    const copy = getErrorCopy(statusCode)
    const hasHttpStatus = statusCode >= 400

    const statusMessage = (
      options.statusMessage
      ?? readString(inputRecord?.statusMessage)
      ?? readString(inputRecord?.statusText)
      ?? readString(response?.statusText)
      ?? copy.statusMessage
    )

    const message = (
      options.message
      ?? (typeof input === 'string' ? input : undefined)
      ?? (!hasHttpStatus ? readString(inputRecord?.message) : undefined)
      ?? copy.message
    )

    const description = (
      options.description
      ?? getErrorDescription(responseData)
      ?? getErrorDescription(inputRecord?.data)
      ?? (!hasHttpStatus ? readString(inputRecord?.message) : undefined)
      ?? copy.description
    )

    const existingData = isRecord(inputRecord?.data) ? inputRecord.data : {}
    const customData = isRecord(options.data) ? options.data : {}

    return createError({
      statusCode,
      statusMessage,
      message,
      fatal: options.fatal ?? true,
      data: {
        ...existingData,
        ...customData,
        description,
        retryTo: options.retryTo ?? route.fullPath ?? '/',
        retryLabel: options.retryLabel ?? 'Try again',
        backTo: options.backTo ?? '/',
        backLabel: options.backLabel ?? 'Back home',
        request: getRequestPath(options.request) ?? getRequestPath(inputRecord?.request as RequestInfo | undefined),
      } satisfies AppErrorData,
      cause: options.cause ?? (input instanceof Error ? input : inputRecord?.cause),
    })
  }

  const showAppError = (input: unknown, options: AppErrorOptions = {}) => {
    return showError(createAppError(input, options))
  }

  const throwAppError = (input: unknown, options: AppErrorOptions = {}): never => {
    throw createAppError(input, options)
  }

  const clearAppError = async (redirect = '/') => {
    await clearError({ redirect })
  }

  return {
    clearAppError,
    createAppError,
    showAppError,
    throwAppError,
  }
}
